import styled, { useTheme } from 'styled-components';
import { CheckMarkIcon } from '../icons';
import { addAlpha } from '@algoan/deep-dive/theme';

export enum CheckedState {
  TRUE,
  FALSE,
  INTERMEDIATE,
}

export interface CheckBoxProps {
  checked: CheckedState;
  onCheck?: () => void;
  variant?: 'primary' | 'secondary' | 'deprecated';
  dataTestId?: string;
}

export const booleanToCheckedState = (booleanValue: boolean): CheckedState =>
  booleanValue ? CheckedState.TRUE : CheckedState.FALSE;

export const CheckBox: React.FC<CheckBoxProps> = ({ checked, onCheck, variant = 'primary', dataTestId }) => {
  const theme = useTheme();
  switch (variant) {
    case 'primary':
      const isChecked = checked === CheckedState.TRUE;
      return (
        <S.PrimaryContainer data-testid={dataTestId} checked={isChecked} onClick={onCheck}>
          {isChecked ? <S.PrimaryCheckMarkIcon color={theme.white} /> : undefined}
        </S.PrimaryContainer>
      );
    case 'secondary':
      return (
        <S.SecondaryContainer data-testid={dataTestId} onClick={onCheck}>
          {checked === CheckedState.TRUE ? <S.SecondaryCheckMarkIcon /> : undefined}
        </S.SecondaryContainer>
      );
    case 'deprecated':
      return (
        <S.CheckBoxContainer data-testid={dataTestId} onClick={onCheck}>
          <S.CheckBox checked={checked} />
        </S.CheckBoxContainer>
      );
  }
};

const S = {
  CheckBoxContainer: styled.div`
    display: flex;
    justify-content: center;
    position: relative;
  `,
  CheckBox: styled.div<{ checked: CheckedState }>`
    background-color: #ffffff;
    ${(props) => {
      switch (props.checked) {
        case CheckedState.INTERMEDIATE:
          return 'background-image: url(/images/vertical-bar.png);';
        case CheckedState.TRUE:
          return 'background-image: url(/images/tick.png);';
        default:
          return '';
      }
    }};
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    border-radius: 3px;
    border: 1px solid #bacfdb;
    box-sizing: border-box;
    height: 16px;
    margin: 1px 0;
    width: 16px;
  `,
  PrimaryContainer: styled.div<{ checked: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 24px;
    width: 24px;
    border-radius: 3px;
    border: 1px solid ${({ theme, checked }) => (checked ? theme.main.deepBlue : theme.secondary.softBlue1)};
    background: ${({ theme, checked }) => (checked ? theme.main.deepBlue : theme.white)};
    cursor: pointer;

    &:hover {
      border-color: ${({ theme, checked }) => (checked ? theme.main.deepBlue : theme.secondary.softBlue2)};
    }
  `,
  SecondaryContainer: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 24px;
    width: 24px;
    border-radius: 3px;
    border: 1px solid ${(props) => addAlpha(props.theme.algoanBlue, 0.08)};
    background: ${({ theme }) => theme.white};
    cursor: pointer;
  `,
  PrimaryCheckMarkIcon: styled(CheckMarkIcon)`
    width: 13px;
    height: 10px;
  `,
  SecondaryCheckMarkIcon: styled(CheckMarkIcon)``,
};
